/**
 * Configuración de Estilos de Mapa - WESTEER
 *
 * @version 1.0
 * @module core/map-engine
 * @updated 2026-02-10
 *
 * @description
 * Configuración centralizada y modular de estilos de mapa.
 * Define los 5 estilos disponibles con sus metadatos y URLs.
 *
 * @remarks
 * **¿Por qué un archivo separado?**
 * - Facilita ajustes sin tocar el core de MapStyleService
 * - Permite agregar/quitar estilos fácilmente
 * - Centraliza toda la info de estilos (nombre, descripción, provider)
 * - Previene el problema de objetos que desaparecen al cambiar estilos
 *
 * **Estilos disponibles**:
 * 1. dark: Oscuro profesional (default, optimizado para trabajo prolongado)
 * 2. light: Claro minimalista (para presentaciones/impresión)
 * 3. streets: Vial detallado (estilo OSM, ideal para análisis de redes)
 * 4. terrain: Relieve y geografía (topografía, elevaciones)
 * 5. satellite: Imagen satelital (contexto real del territorio)
 *
 * **Providers soportados**:
 * - MapTiler (requiere API key en VITE_MAPTILER_API_KEY)
 * - MapLibre Demo (fallback sin API key)
 */

/**
 * Tipos de estilos de mapa disponibles
 */
export type MapStyleType = 'dark' | 'light' | 'streets' | 'terrain' | 'satellite' | 'solid';

/**
 * Metadata de un estilo de mapa
 */
export interface MapStyleMetadata {
  /**
   * ID único del estilo
   */
  id: MapStyleType;

  /**
   * Nombre del estilo para UI
   */
  name: string;

  /**
   * Descripción breve del estilo
   */
  description: string;

  /**
   * Nombre del estilo en MapTiler
   * @see https://docs.maptiler.com/cloud/api/maps/
   */
  maptilerStyle: string;

  /**
   * URL de fallback (sin API key)
   * Puede ser null si no hay fallback disponible
   */
  fallbackUrl: string | null;

  /**
   * URL de la miniatura del estilo
   * Imagen de preview (recomendado: 400x300px o similar)
   */
  thumbnailUrl: string;

  /**
   * Orden de aparición en selector
   */
  order: number;

  /**
   * Recomendado para qué tipo de análisis
   */
  recommendedFor: string[];
}

/**
 * Configuración de los 5 estilos de mapa
 *
 * @remarks
 * **Guía de ajuste**:
 * - Para cambiar un estilo: modificar `maptilerStyle` con el nombre correcto de MapTiler
 * - Para agregar un estilo: agregar nueva entrada con todos los campos
 * - Para quitar un estilo: remover la entrada y actualizar MapStyleType
 * - Para fallback: usar 'https://demotiles.maplibre.org/style.json' o null
 *
 * **MapTiler Styles disponibles** (requieren API key):
 * @see https://cloud.maptiler.com/maps/
 * - streets-v2-dark
 * - streets-v2-light
 * - streets-v2 (vial detallado)
 * - outdoor-v2 (terrain/topografía)
 * - satellite (imagen satelital)
 * - hybrid (satelital con etiquetas)
 * - topo-v2 (topográfico)
 * - winter-v2 (temático invernal)
 */
export const MAP_STYLES_CONFIG: Record<MapStyleType, MapStyleMetadata> = {
  /**
   * Estilo DARK - Default de WESTEER
   *
   * @remarks
   * - Diseñado para trabajo prolongado (reducción de fatiga visual)
   * - Optimizado para análisis urbano en pantalla
   * - Resalta layers y datos sobre el mapa
   */
  dark: {
    id: 'dark',
    name: 'Oscuro',
    description: 'Mapa oscuro profesional para trabajo prolongado',
    maptilerStyle: 'streets-v2-dark',
    fallbackUrl: 'https://demotiles.maplibre.org/style.json',
    thumbnailUrl: '/images/map-styles/dark.png',
    order: 1,
    recommendedFor: ['Trabajo prolongado', 'Análisis nocturno', 'Visualización de datos'],
  },

  /**
   * Estilo LIGHT - Para presentaciones
   *
   * @remarks
   * - Fondo claro minimalista
   * - Ideal para impresión y presentaciones
   * - Mayor contraste en proyecciones
   */
  light: {
    id: 'light',
    name: 'Claro',
    description: 'Mapa claro minimalista para presentaciones e impresión',
    maptilerStyle: 'streets-v2-light',
    fallbackUrl: null, // No hay fallback claro disponible en MapLibre demo
    thumbnailUrl: '/images/map-styles/light.png',
    order: 2,
    recommendedFor: ['Presentaciones', 'Impresión', 'Exportación de mapas'],
  },

  /**
   * Estilo STREETS - Vial detallado (OSM-like)
   *
   * @remarks
   * - Muestra red vial con máximo detalle
   * - Estilo similar a OpenStreetMap
   * - Ideal para análisis de movilidad y transporte
   */
  streets: {
    id: 'streets',
    name: 'Vial',
    description: 'Red vial detallada estilo OpenStreetMap',
    maptilerStyle: 'streets-v2',
    fallbackUrl: 'https://demotiles.maplibre.org/style.json',
    thumbnailUrl: '/images/map-styles/streets.png',
    order: 3,
    recommendedFor: ['Análisis de movilidad', 'Planificación de rutas', 'Redes de transporte'],
  },

  /**
   * Estilo TERRAIN - Relieve y geografía
   *
   * @remarks
   * - Muestra topografía, elevaciones, curvas de nivel
   * - Ideal para análisis de terreno
   * - Visualiza pendientes y geografía física
   */
  terrain: {
    id: 'terrain',
    name: 'Relieve',
    description: 'Topografía con elevaciones y curvas de nivel',
    maptilerStyle: 'outdoor-v2',
    fallbackUrl: null, // No hay fallback de terrain en MapLibre demo
    thumbnailUrl: '/images/map-styles/terrain.png',
    order: 4,
    recommendedFor: ['Análisis de pendientes', 'Estudios geográficos', 'Planificación territorial'],
  },

  /**
   * Estilo SATELLITE - Imagen satelital
   *
   * @remarks
   * - Imagen real del territorio
   * - Contexto visual del terreno
   * - Ideal para verificación en campo
   */
  satellite: {
    id: 'satellite',
    name: 'Satelital',
    description: 'Imagen satelital con calles y etiquetas',
    maptilerStyle: 'hybrid',
    fallbackUrl: null, // No hay fallback satelital en MapLibre demo
    thumbnailUrl: '/images/map-styles/satellite.png',
    order: 5,
    recommendedFor: ['Contexto real', 'Verificación de campo', 'Análisis ambiental'],
  },

  /**
   * Estilo SOLID - Fondo liso con color configurable
   *
   * @remarks
   * - Fondo de color sólido sin mapa base
   * - Color configurable mediante color picker
   * - Ideal para enfocarse solo en las capas de datos
   * - No requiere API key ni conexión a internet
   */
  solid: {
    id: 'solid',
    name: 'Fondo Liso',
    description: 'Color sólido configurable sin mapa base',
    maptilerStyle: '', // No aplica - se genera inline
    fallbackUrl: 'inline', // Marcador especial para generar inline
    thumbnailUrl: '/images/map-styles/solid.png',
    order: 6,
    recommendedFor: ['Presentaciones', 'Enfoque en datos', 'Mapas esquemáticos'],
  },
};

/**
 * Obtiene todos los estilos ordenados
 *
 * @returns Array de metadatos de estilos ordenados
 */
export function getAllStyles(): MapStyleMetadata[] {
  return Object.values(MAP_STYLES_CONFIG).sort((a, b) => a.order - b.order);
}

/**
 * Obtiene metadata de un estilo específico
 *
 * @param type - Tipo de estilo
 * @returns Metadata del estilo
 * @throws Error si el tipo no existe
 */
export function getStyleMetadata(type: MapStyleType): MapStyleMetadata {
  const metadata = MAP_STYLES_CONFIG[type];
  if (!metadata) {
    throw new Error(`Estilo de mapa no encontrado: ${type}`);
  }
  return metadata;
}

/**
 * Valida si un string es un tipo de estilo válido
 *
 * @param value - String a validar
 * @returns true si es un MapStyleType válido
 */
export function isValidStyleType(value: string): value is MapStyleType {
  return value in MAP_STYLES_CONFIG;
}
